import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { fetchLeaderboard } from "../../functions/fetchLeaderboard";
import { setBackButtonUrl } from "../uiSlice";
import "./leaderboard.css";

/* Static sparkle positions */
const SPARKLES = [
  { top: "8%",  left: "6%",  delay: "0s",    size: 8 },
  { top: "15%", left: "88%", delay: "1s",    size: 6 },
  { top: "35%", left: "92%", delay: "0.5s",  size: 5 },
  { top: "60%", left: "4%",  delay: "1.5s",  size: 7 },
  { top: "75%", left: "82%", delay: "0.8s",  size: 5 },
  { top: "90%", left: "18%", delay: "0.3s",  size: 6 },
];

function Leaderboard() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const dispatch  = useDispatch();
  const { user }  = useSelector((state) => state.auth);

  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError  ] = useState(null);

  useEffect(() => {
    dispatch(setBackButtonUrl("/leaderboard"));
  }, [dispatch]);

  // Keep user on leaderboard even when browser back is pressed.
  useEffect(() => {
    const push = () => {
      try {
        window.history.pushState(null, "", window.location.href);
      } catch {
        // ignore
      }
    };
    // Add one extra entry so "Back" triggers popstate here.
    push();
    const onPopState = (e) => {
      e?.preventDefault?.();
      push();
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  /* ── resolve current user ── */
  const storedUserData = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData") || "{}";
      return JSON.parse(raw) || {};
    } catch { return {}; }
  }, []);

  const currentUserId = useMemo(() => (
    user?.userId ?? user?.userid ?? user?.id ??
    storedUserData.userId ?? storedUserData.userid ?? storedUserData.id
  ), [user, storedUserData]);

  /* ── session / org token ── */
  const sessionId      = location.state?.sessionId      || storedUserData.sessionId;
  const organizationId = location.state?.organizationId || storedUserData.organizationId;
  const token          = sessionId && organizationId ? `${sessionId}&${organizationId}` : "";

  /* ── fetch ── */
  useEffect(() => {
    if (!token || !sessionId || !organizationId) {
      setError("Missing session data");
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data   = await fetchLeaderboard({ token, sessionId, organizationId });
        if (cancelled) return;
        const sorted = Array.isArray(data)
          ? data.sort((a, b) => (Number(b.total_score) || 0) - (Number(a.total_score) || 0)).slice(0, 10)
          : [];
        setLeaders(sorted);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load leaderboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [token, sessionId, organizationId]);

  /* ── helpers ── */
  const isCurrentUser = (userId) => String(userId) === String(currentUserId);

  const topThree = leaders.slice(0, 3);
  const restSeven = leaders.slice(3, 10);

  /* ordinal label */
  const ordinal = (n) => {
    if (n === 1) return "1st";
    if (n === 2) return "2nd";
    if (n === 3) return "3rd";
    return `${n}th`;
  };

  /* badge icon per rank */
  const rankIcon = (rank) => {
    if (rank === 2) return (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="18" cy="22" r="13" fill="#C0C0C0" opacity="0.3"/>
        <circle cx="18" cy="20" r="12" fill="url(#silver)"/>
        <text x="18" y="26" textAnchor="middle" fontSize="13" fontWeight="900" fill="white">2</text>
        <defs>
          <linearGradient id="silver" x1="6" y1="8" x2="30" y2="32" gradientUnits="userSpaceOnUse">
            <stop stopColor="#e8e8e8"/>
            <stop offset="1" stopColor="#9a9a9a"/>
          </linearGradient>
        </defs>
        {/* ribbon */}
        <path d="M12 10 L18 2 L24 10 L21 8 L18 14 L15 8 Z" fill="#a0a0a0"/>
      </svg>
    );
    if (rank === 3) return (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="18" cy="22" r="13" fill="#cd7f32" opacity="0.3"/>
        <circle cx="18" cy="20" r="12" fill="url(#bronze)"/>
        <text x="18" y="26" textAnchor="middle" fontSize="13" fontWeight="900" fill="white">3</text>
        <defs>
          <linearGradient id="bronze" x1="6" y1="8" x2="30" y2="32" gradientUnits="userSpaceOnUse">
            <stop stopColor="#e8a060"/>
            <stop offset="1" stopColor="#9a4a10"/>
          </linearGradient>
        </defs>
        <path d="M12 10 L18 2 L24 10 L21 8 L18 14 L15 8 Z" fill="#b06020"/>
      </svg>
    );
    return null;
  };

  /* trophy for 3rd in reference ui */
  const TrophyIcon = () => (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 6h16v12a8 8 0 01-16 0V6z" fill="#d4902a" opacity="0.85"/>
      <path d="M10 10H6a4 4 0 004 4" stroke="#d4902a" strokeWidth="2" fill="none"/>
      <path d="M26 10h4a4 4 0 01-4 4" stroke="#d4902a" strokeWidth="2" fill="none"/>
      <rect x="15" y="24" width="6" height="4" fill="#d4902a" opacity="0.7"/>
      <rect x="12" y="28" width="12" height="2.5" rx="1.2" fill="#d4902a"/>
    </svg>
  );

  /* ── render ── */
  return (
    <div className="leaderboard-page">
      {/* Decorative sparkles */}
      {SPARKLES.map((s, i) => (
        <div
          key={i}
          className="sparkle"
          style={{
            top: s.top,
            left: s.left,
            width: s.size,
            height: s.size,
            animationDelay: s.delay,
          }}
        />
      ))}

      <div className="leaderboard-header">
        <h1 className="leaderboard-title">Leaderboard</h1>
      </div>

      {loading ? (
        <div className="leaderboard-loading">Loading leaderboard…</div>
      ) : error ? (
        <div className="leaderboard-error">{error}</div>
      ) : leaders.length === 0 ? (
        <div className="leaderboard-empty">No leaderboard data available</div>
      ) : (
        <div className="leaderboard-wrapper">

          {/* ── Top 3 Podium ── */}
          <div className="leaderboard-top-three">
            {topThree.map((leader, idx) => {
              const rank   = idx + 1;
              const isUser = isCurrentUser(leader.userid);
              const name   = leader.name || leader.userid || "—";

              return (
                <div
                  key={`top-${rank}`}
                  className={`leaderboard-podium podium-rank-${rank}${isUser ? " current-user" : ""}`}
                  style={{ animationDelay: `${idx * 0.15}s` }}
                >
                  {/* Crown floats above rank-1 */}
                  {rank === 1 && <span className="podium-crown">👑</span>}

                  {/* Medal icon for rank 2/3 */}
                  <span className="podium-badge">
                    {rank === 2 && rankIcon(2)}
                    {rank === 3 && <TrophyIcon />}
                  </span>

                  <div className="podium-rank-label">{ordinal(rank)}</div>

                  <div className="podium-name">
                    {name}
                    {isUser && <span className="you-tag">(You)</span>}
                  </div>

                  <div className="podium-score">
                    Score: {Number(leader.total_score) || 0}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Positions 4-10 ── */}
          <div className="leaderboard-bottom-list">
            {restSeven.map((leader, idx) => {
              const rank   = idx + 4;
              const isUser = isCurrentUser(leader.userid);
              const name   = leader.name || leader.userid || "—";

              return (
                <div
                  key={`bottom-${rank}`}
                  className={`leaderboard-row${isUser ? " current-user" : ""}`}
                  style={{ animationDelay: `${(idx + 3) * 0.08}s` }}
                >
                  <div className="row-rank">{rank}</div>
                  <div className="row-name">
                    {name}
                    {isUser && <span className="you-tag"> (You)</span>}
                  </div>
                  <div className="row-score">{Number(leader.total_score) || 0}</div>
                </div>
              );
            })}
          </div>

        </div>
      )}

  
    </div>
  );
}

export default Leaderboard;
